import React, { FC, useEffect, useState } from 'react';
import { useApolloClient } from 'react-apollo';
import { useHistory, useParams } from 'react-router-dom';

import { Content, useGetContentLazyQuery, useGetSharePreviewInfoLazyQuery } from 'api';
import { Content as ContentFragment } from 'api/fragments';
import { Events } from 'analytics';
import { Layouts } from 'components';
import { useNotify } from 'hooks';
import { ContentProvider } from 'providers';
import * as paths from 'paths';

import { Form, Loader } from '../components/ContentShare';

interface IParams {
  content_id: string;
}

const ContentShare: FC = () => {
  const apolloClient = useApolloClient();
  const history = useHistory();
  const [cachedContent, setCachedContent] = useState<Content>();
  const { content_id } = useParams<IParams>();
  const notify = useNotify();

  const [getContentQuery, getContentResult] = useGetContentLazyQuery({
    onError: notify.queryError,
    variables: { content_id },
  });
  const [getSharePreviewInfoQuery, getSharePreviewResult] = useGetSharePreviewInfoLazyQuery({
    onError: notify.queryError,
    variables: { content_id },
  });

  useEffect(() => {
    analytics.track(Events.ShareInitiated, { content_id });

    const cachedContentResult = findCachedContent();
    if (cachedContentResult) {
      setCachedContent(cachedContentResult);
    } else {
      getContentQuery();
    }
    getSharePreviewInfoQuery();
  }, []);

  function findCachedContent() {
    return apolloClient.readFragment({
      id: content_id,
      fragment: ContentFragment,
      fragmentName: 'Content',
    });
  }

  function isLoading() {
    return getContentResult.loading || getSharePreviewResult.loading;
  }

  function handleClose() {
    history.push(paths.index);
  }

  function hasData() {
    return (cachedContent || getContentResult.data) && getSharePreviewResult.data;
  }

  function hasResults() {
    if (!hasData) {
      return false;
    }

    return (
      (cachedContent || getContentResult.data.getContent) &&
      getSharePreviewResult.data.getSharePreviewInfo
    );
  }

  function renderMain() {
    if (isLoading()) {
      return <Loader />;
    }

    if (!hasData() || !hasResults()) {
      return null;
    }

    const content = cachedContent || getContentResult.data.getContent;
    const { getSharePreviewInfo } = getSharePreviewResult.data;

    return (
      <ContentProvider value={{ content }}>
        <Form
          previewInfo={getSharePreviewInfo}
          onConnect={getSharePreviewResult.refetch}
          onClose={handleClose}
        />
      </ContentProvider>
    );
  }

  return <Layouts.App1Column navigation desktopSize={12} main={renderMain()} />;
};

export default ContentShare;
